import styled from "@emotion/styled/macro";
import { MESSAGE_TYPE } from "@home-health-notify/domain";
import InfoIcon from "@mui/icons-material/Info";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ReplyAllIcon from "@mui/icons-material/ReplyAll";
import {
  alpha,
  Box,
  Chip,
  css,
  IconButton,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";
import { Theme } from "@mui/material/styles/createTheme";
import { RESPONSIVE_DRAWER, shouldForwardProp } from "../common/Common.style";

type StyleBoxProps = {
  isSender?: boolean;
  messageType?: string;
  isForwardMessagesOn?: boolean;
  isChecked?: boolean;
};

export const mentionInputClassName = "hhn-mention";

export const MassagePanelWrapper = styled(Box)`
  position: relative;
  height: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column-reverse;
  background: ${({ theme }) => (theme as Theme).messagePanelBackground};
  flex-shrink: 1;
`;

export const MassagePanelComponent = styled(Box)`
  position: absolute;
  top: 0;
  z-index: 1;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: scroll;
  transition: background 0.3s ease-out 0.1s;
  background-color: initial;
  left: 0;
  scroll-behavior: smooth;
`;

export const InputMessageComponent = styled(Box)`
  box-sizing: border-box;
  padding: 8px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  min-width: 0;
  min-height: 62px;
  border-top: 1px solid ${({ theme }) => (theme as Theme).palette.divider};
  position: relative;
  z-index: 1;

  & .hhn-mention {
    box-sizing: border-box;
    display: block;
    position: relative;
    padding: 0;
    border: none;
    vertical-align: top;
    flex: 1;
    width: 100%;
    min-width: 0;
    max-height: 120px;
  }

  & .hhn-mention__control {
    box-sizing: border-box;
    position: relative;
    display: block;
    width: 100%;
    min-width: 0;
    padding: 0;

    font-family: "Roboto", "Helvetica", "Arial", sans-serif;
    font-weight: 400;
    font-size: 1rem;
    line-height: 1.4375em;
    letter-spacing: 0.00938em;

    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.23);

    &:focus-within {
      border-color: ${({ theme }) => (theme as Theme).palette.primary.main};
    }
  }

  & .hhn-mention__input,
  & .hhn-mention__highlighter {
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    max-height: 114px;
    padding: 10px 14px;
    border: none;

    font-family: inherit;
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    letter-spacing: inherit;

    white-space: pre-wrap;
    word-break: break-word;
    overflow-wrap: break-word;

    scrollbar-gutter: stable;
    overflow-x: hidden;
    overflow-y: scroll;
  }

  & .hhn-mention__input {
    resize: none;
    overflow-x: hidden !important;
    overflow-y: scroll !important;

    &:focus {
      outline: 0;
    }

    &::placeholder {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  & .hhn-mention__highlighter {
    pointer-events: none;
    scrollbar-color: transparent transparent;

    & strong {
      margin: 0;
      padding: 0;
      border: 0;

      font-family: inherit;
      font-size: inherit;
      font-weight: inherit;
      font-style: inherit;
      line-height: inherit;
      letter-spacing: inherit;

      background-color: ${({ theme }) =>
        alpha((theme as Theme).palette.primary.light, 0.5)};
      border-radius: 2px;
    }
  }

  & .hhn-mention__suggestions {
    background-color: ${({ theme }) =>
      (theme as Theme).palette.background.paper};
    margin-top: 24px !important;
    border: 1px solid
      ${({ theme }) => (theme as Theme).palette.action.disabledBackground};
    box-shadow: 0 0 8px
      ${({ theme }) => (theme as Theme).palette.action.disabled};
    width: max-content;
    overflow: auto;
    max-height: 400px;
  }

  & .hhn-mention__suggestions__item {
    display: flex;
    align-items: center;
    height: 48px;
  }

  & .hhn-mention__suggestions__item--focused {
    background-color: ${({ theme }) =>
      (theme as Theme).palette.action.selected};
  }
`;

export const PendingReferralWrapper = styled(Box)`
  padding: 8px 20px 8px 30px;
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  min-width: 0;

  & + & {
    border-top: 1px solid ${({ theme }) => (theme as Theme).palette.divider};
  }
`;

export const CaptionWrapper = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 94px;
  border-top: 1px solid ${({ theme }) => (theme as Theme).palette.divider};
  flex-shrink: 0;
`;

export const ForwardMessagesActionPanelWrapper = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 94px;
  border-top: 1px solid ${({ theme }) => (theme as Theme).palette.divider};
  background: ${({ theme }) => (theme as Theme).palette.grey[200]};
`;

export const CloseForwardMessagesPanelWrapper = styled(Box)`
  margin-left: 30px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 13px;
`;

export const CloseForwardMessagesPanel = styled(IconButton)`
  color: ${({ theme }) => (theme as Theme).palette.grey[600]};
`;

export const ForwardedIcon = styled(ReplyAllIcon)`
  font-size: large;
  transform: scaleX(-1);
`;

export const ForwardMessagesIconButton = styled(IconButton)`
  margin-right: 30px;
`;

export const ForwardMessagesIcon = styled(ForwardedIcon)`
  font-size: 25px;
  color: ${({ theme }) => (theme as Theme).palette.grey[600]};
`;

export const TotalMessagesSelectedText = styled(Typography)`
  font-weight: 500;
`;

export const MessageBubble = styled(Paper, {
  shouldForwardProp: prop =>
    shouldForwardProp<{
      messageBubbleFullWidth?: boolean;
      bubbleBackgroundColor: string;
    }>(["messageBubbleFullWidth", "bubbleBackgroundColor"], prop),
})<{
  messageBubbleFullWidth?: boolean;
  bubbleBackgroundColor: string;
}>(({ messageBubbleFullWidth, bubbleBackgroundColor }) => ({
  display: "inline-block",
  minWidth: "256px",
  borderRadius: "7px",
  position: "relative",
  width: messageBubbleFullWidth ? "100%" : "auto",
  wordBreak: "break-word",
  backgroundColor: bubbleBackgroundColor,
}));

export const MessagePanelBubbleWithForwardOption = styled(Box)`
  display: flex;
  margin-bottom: 10px;
`;

export const BubbleMenu = styled(Box, {
  shouldForwardProp: prop =>
    shouldForwardProp<{
      keepMenuVisible: boolean;
    }>(["keepMenuVisible"], prop),
})<{
  keepMenuVisible: boolean;
}>(
  ({ keepMenuVisible, theme }) => css`
    position: absolute;
    top: 3px;
    z-index: 800;
    width: 100px;
    max-width: 70%;
    height: 40px;
    overflow: hidden;
    right: 3px;
    border-top-right-radius: 6px;
    background: linear-gradient(
      28deg,
      transparent,
      transparent 40%,
      rgba(0, 0, 0, 0.3) 76%,
      rgba(0, 0, 0, 0.1)
    );
    padding: 3px;
    display: ${keepMenuVisible ? "flex" : "none"};
    flex-direction: column;
    align-items: flex-end;
    color: ${(theme as Theme).palette.common.white};

    ${MessageBubble}:hover & {
      display: flex;
    }
  `,
);

export const BubbleMenuIcon = styled(KeyboardArrowDownIcon)`
  cursor: pointer;
`;

export const MessagePanelBubble = styled(Box, {
  shouldForwardProp: prop =>
    shouldForwardProp<StyleBoxProps>(
      ["isSender", "messageType", "isForwardMessagesOn", "isChecked"],
      prop,
    ),
})<StyleBoxProps>(({
  isSender,
  messageType,
  isForwardMessagesOn,
  isChecked,
  theme,
}) => {
  const infoBubbleColor = (theme as Theme)?.message?.infoBubble ?? "#f1f1f1";
  let hoverStyle: any = undefined;
  if (isForwardMessagesOn && !isChecked && messageType !== MESSAGE_TYPE.INFO) {
    hoverStyle = { backgroundColor: infoBubbleColor };
  }
  return {
    display: messageType === MESSAGE_TYPE.INFO || isSender ? "flex" : "inherit",
    justifyContent:
      messageType === MESSAGE_TYPE.INFO
        ? "center"
        : isSender && !isForwardMessagesOn
          ? "flex-end"
          : isSender && isForwardMessagesOn
            ? "space-between"
            : "inherit",
    width: "100%",
    position: "relative",
    "&:hover": hoverStyle,
    cursor:
      isForwardMessagesOn && messageType !== MESSAGE_TYPE.INFO
        ? "pointer"
        : undefined,
  };
});

export const ProgressLayout = styled(Box)`
  width: 40%;
  position: absolute;
  z-index: 2;
  bottom: 8px;
  padding: 0 8px 0 10px;
  left: 0;
  right: 0;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => (theme as Theme).palette.primary.main};
  border-radius: 20px;
  background-color: #f1f5f8;
  scale: 0.88;
`;

export const LinearProgressLayout = styled(LinearProgress)`
  flex: 1;
  margin-left: 16px;
`;

export const PriorityMessageContainer = styled(Box)`
  position: absolute;
  z-index: 2;
  top: -28px;
  width: 100%;
  display: flex;
  justify-content: center;
`;

export const PriorityChip = styled(Chip)`
  font-weight: 400;
  font-size: 14px;
`;

export const HighlightedOverlay = styled(Box)`
  height: 100%;
  width: 100%;
  position: absolute;
  z-index: 1;
  background-color: ${({ theme }) =>
    (theme as Theme)?.message?.infoBubble ?? "#f1f1f1"};
`;

export const MessagesContainer = styled(Box)`
  display: flex;
  flex-direction: column-reverse;
  height: 100%;
  overflow: auto;
`;

type MessageBubbleWithAvatarProps = {
  messageBubbleFullWidth?: boolean;
  isSender?: boolean;
  showMessageInfo?: boolean;
};

export const MessageBubbleWithAvatar = styled(Box, {
  shouldForwardProp: prop =>
    shouldForwardProp<MessageBubbleWithAvatarProps>(
      ["messageBubbleFullWidth", "isSender", "showMessageInfo"],
      prop,
    ),
})<MessageBubbleWithAvatarProps>(
  ({ messageBubbleFullWidth, isSender, showMessageInfo, theme }) => ({
    display: "flex",
    flexDirection: isSender ? "row-reverse" : "row",
    maxWidth: showMessageInfo || messageBubbleFullWidth ? "100%" : "75%",
    width: messageBubbleFullWidth ? "100%" : "auto",
    margin: showMessageInfo ? 0 : "10px",
    gap: "10px",
    padding: showMessageInfo ? "0px 7px" : 0,
    // At narrow viewports the message panel can be tight enough that the
    // default 75% cap clips fixed-width content like the audio control. Bump
    // to 88% below 1280 so the bubble has enough room without overlapping
    // the chat info drawer.
    [`@media (max-width: ${RESPONSIVE_DRAWER.WIDE_BP - 1}px)`]: {
      maxWidth: showMessageInfo || messageBubbleFullWidth ? "100%" : "88%",
    },
  }),
);

export const ProgressLoaderWrapper = styled(Box)`
  width: 94%;
  height: 100%;
  margin: 20px;
`;

export const MessagePanelItemContainer = styled(Box)``;
export const SpeedDialContainer = styled(Box)``;

export const MessageInputContainer = styled(Box)`
  display: flex;
  width: 100%;
  align-items: center;
`;

export const ReplyMessageInputBox = styled(Box)`
  width: 100%;
`;

export const MessageInputBox = styled(Box)`
  margin-left: 45px;
`;

export const MessageActionButton = styled(IconButton)`
  padding: 10px;
`;

export const MessageActionsContainer = styled(Box)`
  display: flex;
  flex-direction: column;
`;

export const MessageActionsButtons = styled(Box)`
  display: flex;
  justify-content: end;
  align-items: center;
`;

export const PendingReferralsText = styled(Typography)`
  font-weight: 500;
  margin-left: 6px;
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const InputMessageContainer = styled(Box)`
  flex-shrink: 0;
  min-width: 0;
`;

export const MessagePanelContainerWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  overflow: auto;
  height: 100%;
  min-width: 0;
`;

export const ForwardedMessageBox = styled(Box)`
  padding: 2px 12px;
  display: flex;
  align-items: center;
  color: ${({ theme }) => (theme as Theme).palette.grey[600]};
`;

export const InfoIconContainer = styled(InfoIcon)`
  font-size: large;
  cursor: pointer;
  transition: cursor 0.3s ease-in-out;
`;

export const ForwardedText = styled(Typography)`
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => (theme as Theme).palette.grey[600]};
`;
export const EditMessageContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-width: 0;
  padding: 4px 8px 4px 16px;
  box-sizing: border-box;
`;

export const EditMessageText = styled(Typography)`
  flex: 1;
  min-width: 0;
`;

export const CancelEditMessageButton = styled(IconButton)`
  flex-shrink: 0;
`;
